import { Middleware, AnyAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import AuthService from "../services/AuthService";
import { history } from "./index";

// Constants - export them to be used by other middlewares
export const REQUEST_SUBTYPE = "_REQUEST";
export const SUCCESS_SUBTYPE = "_SUCCESS";
export const FAILURE_SUBTYPE = "_FAILURE";
const ERROR_STATUSES = [400, 401, 403, 404, 500];
const authPath = "/account/signin";

// Define action types for better type checking
interface ApiCallAction extends AnyAction {
  type: string;
  apiCall?: () => Promise<any>;
  shouldCallAPI?: (state: any) => boolean;
}

/**
 * Middleware for handling API calls in Redux.
 * Compatible with Redux Toolkit.
 */
const apiCallMiddleware: Middleware =
  ({ dispatch, getState }) => (next) => (action) => {
    const { type, apiCall, shouldCallAPI } = action as ApiCallAction;

    // Normal action: pass it on
    if (!apiCall) {
      return next(action);
    }

    // Optional: Check if API should be called based on current state
    if (shouldCallAPI && !shouldCallAPI(getState())) {
      return Promise.resolve();
    }

    const requestType = `${type}${REQUEST_SUBTYPE}`;
    const successType = `${type}${SUCCESS_SUBTYPE}`;
    const failureType = `${type}${FAILURE_SUBTYPE}`;

    // Dispatch request action
    dispatch({ type, subtype: requestType, payload: {} });

    // Execute API call
    return apiCall()
      .then((response: any) => {
        // Handle response data
        const { data } = response;
        // Extract data.data from Backend API (if available) or use data directly
        const result = data?.data !== undefined ? data.data : data;

        // Dispatch success action
        dispatch({ type, subtype: successType, payload: { result } });

        // Return result for chaining
        return result;
      })
      .catch((err: any) => {
        const error = (err as unknown as AxiosError).response;

        // Handle API errors
        if (error?.status && ERROR_STATUSES.includes(error.status)) {
          dispatch({ type, subtype: failureType, payload: { ...error } });

          // Handle unauthorized error (401)
          if (error.status === 401) {
            const { pathname } = window.location;

            AuthService.resetToken();
            if (pathname !== authPath) {
              sessionStorage.setItem("pendingRoute", pathname);
              history.push(authPath);
            }
          }
        } else {
          dispatch({
            type,
            subtype: failureType,
            payload: {
              error: error?.data || err?.message || error,
            },
          });
        }

        // Return rejected promise for error handling in action creators
        return error;
      });
  };

export default apiCallMiddleware;
